import type { MarketVariants } from '@/lib/i18n'

export type Slide9cToolBridgeCopy = {
  title: string
  introBefore: string
  toolsLinkLabel: string
  introAfter: string

  scamCheckerName: string
  scamCheckerSubtitle: string
  tabLink: string
  tabMessage: string
  tabImage: string
  tabSituation: string

  /** Pre-filled SMS shown in the Scam Checker. References a postal service
   *  per market (USPS / Royal Mail / Canada Post / Australia Post / Qatar Post). */
  sampleMessage: string
  /** Domain word baked into the sample message URL, stays in sync with the
   *  finding line so they always reference the same fake domain. */
  fakeDomain: string

  analyseLabel: string
  processing1: string
  processing2: string
  processing3: string

  verdictLabel: string
  verdictSubtitle: string
  finding1: string
  finding2: string
  finding3: string
  recommendationBoldPrefix: string
  recommendation: string
}

export const copy: MarketVariants<Slide9cToolBridgeCopy> = {
  US: {
    title: "When you're not sure",
    introBefore:
      "Not every situation will be as clear-cut as these examples. When you're not sure, the Scam Checker on your ",
    toolsLinkLabel: 'Tools page',
    introAfter:
      ' can look at any link, message, or screenshot and help you decide what to do next.',

    scamCheckerName: 'Scam Checker',
    scamCheckerSubtitle: "Check anything you're unsure about",
    tabLink: 'Link',
    tabMessage: 'Message',
    tabImage: 'Image',
    tabSituation: 'Situation',

    sampleMessage:
      'USPS: Your parcel could not be delivered. Please confirm your address and pay the redelivery fee: usps-redelivery.com/pay',
    fakeDomain: 'usps-redelivery.com',

    analyseLabel: 'Analyze now',
    processing1: 'Checking link safety...',
    processing2: 'Analyzing message language...',
    processing3: 'Comparing against known scams...',

    verdictLabel: 'High risk',
    verdictSubtitle: 'This looks like a delivery scam',
    finding1: 'The domain "usps-redelivery.com" is not owned by USPS.',
    finding2: 'The message creates urgency to push you into acting quickly.',
    finding3:
      'Legitimate delivery services will never ask for payment via text.',
    recommendationBoldPrefix: 'Our advice:',
    recommendation: 'Delete this message. Do not click the link or reply.',
  },
  GB: {
    sampleMessage:
      'Royal Mail: Your parcel could not be delivered. Please confirm your address and pay the redelivery fee: royal-mail-redelivery.com/pay',
    fakeDomain: 'royal-mail-redelivery.com',
    finding1: 'The domain "royal-mail-redelivery.com" is not owned by Royal Mail.',
    // "Analyze" -> "Analyse" auto-handled by toMarketSpelling.
  },
  CA: {
    sampleMessage:
      'Canada Post: Your parcel could not be delivered. Please confirm your address and pay the redelivery fee: canadapost-redelivery.com/pay',
    fakeDomain: 'canadapost-redelivery.com',
    finding1: 'The domain "canadapost-redelivery.com" is not owned by Canada Post.',
  },
  AU: {
    sampleMessage:
      'Australia Post: Your parcel could not be delivered. Please confirm your address and pay the redelivery fee: auspost-redelivery.com/pay',
    fakeDomain: 'auspost-redelivery.com',
    finding1: 'The domain "auspost-redelivery.com" is not owned by Australia Post.',
  },
  QA: {
    sampleMessage:
      'Qatar Post: Your parcel could not be delivered. Please confirm your address and pay the redelivery fee: qatarpost-redelivery.com/pay',
    fakeDomain: 'qatarpost-redelivery.com',
    finding1: 'The domain "qatarpost-redelivery.com" is not owned by Qatar Post.',
    // "Analyze" -> "Analyse" applied at render on the Qatar face.
  },
  ar: {
    title: 'عندما لا تكون متأكداً',
    introBefore:
      'لن تكون كل المواقف واضحة كهذه الأمثلة. عندما لا تكون متأكداً، يمكن لفاحص الاحتيال في ',
    toolsLinkLabel: 'صفحة الأدوات',
    introAfter:
      ' أن يفحص أي رابط أو رسالة أو لقطة شاشة ويساعدك على تحديد خطوتك التالية.',

    scamCheckerName: 'فاحص الاحتيال',
    scamCheckerSubtitle: 'افحص أي شيء لست متأكداً منه',
    tabLink: 'رابط',
    tabMessage: 'رسالة',
    tabImage: 'صورة',
    tabSituation: 'موقف',

    sampleMessage:
      'بريد قطر: تعذّر تسليم طردك. يرجى تأكيد عنوانك ودفع رسوم إعادة التوصيل: qatarpost-redelivery.com/pay',
    fakeDomain: 'qatarpost-redelivery.com',

    analyseLabel: 'افحص الآن',
    processing1: 'جارٍ التحقق من أمان الرابط…',
    processing2: 'جارٍ تحليل لغة الرسالة…',
    processing3: 'جارٍ المقارنة مع عمليات احتيال معروفة…',

    verdictLabel: 'خطورة عالية',
    verdictSubtitle: 'يبدو هذا احتيالاً بذريعة التوصيل',
    finding1: 'النطاق "qatarpost-redelivery.com" لا يعود لبريد قطر.',
    finding2: 'الرسالة تخلق شعوراً بالاستعجال لدفعك إلى التصرّف بسرعة.',
    finding3:
      'شركات التوصيل الحقيقية لن تطلب منك الدفع عبر رسالة نصية أبداً.',
    recommendationBoldPrefix: 'نصيحتنا:',
    recommendation: 'احذف هذه الرسالة. لا تضغط على الرابط ولا تردّ عليها.',
  },
}
